import React, { useState, useEffect, useContext } from "react";

import {
    Scale,
    ArrowRight,
    Sparkles,
    PlayCircle,
    ShieldCheck,
    BarChart3,
    XCircle,
    CheckCircle,
    Download,
    FileText,
    Users,
    Bot,
    Database,
    Settings,
    Search,
    Mail,
    CalendarDays,
    FolderOpen,
    MessageCircle,
    ReceiptText,
    Mic,
    LockKeyhole,
    Gavel,
} from "lucide-react";

import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { isValidPhoneNumber } from "react-phone-number-input";

import Head from "../../Components/Layout/Head";
import FooterNew from "../../Components/Layout/FooterNew";
import { CountryContext } from "../../CountryContext";
import ApiPath from "../../Components/Common/Apiurl";

import AOS from "aos";
import "aos/dist/aos.css";

const countryOptions = [
    "Australia",
    "Germany",
    "India",
    "New Zealand",
    "United States",
    "Other",
];

const legalFeatures = [
    {
        id: 1,
        icon: <FileText size={20} />,
        name: "Smart Document & Draft Assistance",
        purpose: "Legal drafting / document preparation",
        currentChallenge:
            "Lawyers and staff manually prepare notices, agreements, petitions, replies and routine drafts from scattered case inputs.",
        aiAction:
            "AI creates first-level legal drafts from basic matter inputs, selected draft type and preferred language.",
        benefit:
            "Faster first drafts, consistent document structure, fewer formatting mistakes and better focus on legal strategy.",
        implementation:
            "Can support English, Marathi or Hindi templates for notices, agreements, petitions, sale deeds, replies and other reusable legal drafts.",
    },
    {
        id: 2,
        icon: <CalendarDays size={20} />,
        name: "Court Date & Hearing Tracker",
        purpose: "Case calendar / hearing management",
        currentChallenge:
            "Court dates, deadlines and follow-ups are often tracked manually through diaries, Excel sheets or staff reminders.",
        aiAction:
            "AI-enabled tracker organizes hearing dates, deadlines, reminders and case-wise calendar events.",
        benefit:
            "Lower risk of missed dates, better case calendar discipline and improved team preparedness.",
        implementation:
            "Can integrate with case records, Google Calendar, internal reminders, WhatsApp/SMS alerts and role-wise notifications.",
    },
    {
        id: 3,
        icon: <Mail size={20} />,
        name: "Email to Case Data Auto Extraction",
        purpose: "Client email / matter intake",
        currentChallenge:
            "Client emails and attachments are manually read, copied and converted into case data or task records.",
        aiAction:
            "AI reads emails and attachments, extracts key client and matter details, and maps them to structured case records.",
        benefit:
            "Less manual data entry, better organized client information and faster matter creation.",
        implementation:
            "Can extract client name, contact details, case reference, invoice information, requested action, document type and attached files.",
    },
    {
        id: 4,
        icon: <Users size={20} />,
        name: "Task & Team Management",
        purpose: "Internal legal operations",
        currentChallenge:
            "Task ownership, due dates and follow-ups are managed manually, causing coordination gaps between seniors, juniors and admin staff.",
        aiAction:
            "AI-assisted workflow assigns tasks, tracks ownership, monitors progress and highlights pending work.",
        benefit:
            "Better accountability, clearer work allocation and reduced internal follow-up effort.",
        implementation:
            "Can include task assignment, status tracking, priority flags, deadline alerts, user roles and dashboard visibility.",
    },
    {
        id: 5,
        icon: <FolderOpen size={20} />,
        name: "Document Checklist Automation",
        purpose: "Case preparation / document collection",
        currentChallenge:
            "Required document lists are manually prepared for each matter type, increasing the chance of missing important documents.",
        aiAction:
            "AI generates matter-specific document checklists based on case type, draft type or legal workflow.",
        benefit:
            "Faster case preparation, fewer missing documents and better client guidance.",
        implementation:
            "Can support property matters, civil cases, criminal cases, family matters, notices, agreements and compliance-related checklists.",
    },
    {
        id: 6,
        icon: <MessageCircle size={20} />,
        name: "AI Email Reply Suggestions",
        purpose: "Client communication",
        currentChallenge:
            "Client replies require time to draft professionally, especially when matter context must be considered.",
        aiAction:
            "AI suggests professional replies based on email content, case context and preferred tone.",
        benefit:
            "Faster client responses, consistent communication style and improved client experience.",
        implementation:
            "Can generate replies for document requests, hearing updates, payment follow-ups, appointment scheduling and case status communication.",
    },
    {
        id: 7,
        icon: <Database size={20} />,
        name: "Google Drive Integration",
        purpose: "Document access / case repository",
        currentChallenge:
            "Case documents are stored in folders but are difficult to search, link and retrieve quickly during legal work.",
        aiAction:
            "AI connects existing Drive folders with case records and enables structured document search.",
        benefit:
            "Centralized access without forcing the firm to change existing storage habits.",
        implementation:
            "Can map Drive folders to clients, matters, document categories, invoices, drafts, notices and uploaded case files.",
    },
    {
        id: 8,
        icon: <MessageCircle size={20} />,
        name: "WhatsApp / SMS Notifications",
        purpose: "Client updates / reminders",
        currentChallenge:
            "Staff manually call or message clients for hearing reminders, document requests and case updates.",
        aiAction:
            "System sends automated or review-based notifications through WhatsApp/SMS.",
        benefit:
            "Improved client communication, fewer manual calls and better follow-up discipline.",
        implementation:
            "Can support hearing reminders, document pending alerts, invoice reminders, appointment confirmations and status updates.",
    },
    {
        id: 9,
        icon: <ReceiptText size={20} />,
        name: "Invoice & Billing Automation",
        purpose: "Accounts / billing",
        currentChallenge:
            "Invoice creation, payment tracking and billing follow-ups are often handled manually.",
        aiAction:
            "AI-assisted billing creates invoice drafts, tracks payment status and links billing with case records.",
        benefit:
            "Cleaner revenue visibility, fewer missed payments and more organized billing operations.",
        implementation:
            "Can generate editable invoice forms, PDF invoices, payment status dashboards and case-wise billing records.",
    },
    {
        id: 10,
        icon: <Mic size={20} />,
        name: "Audio Chat & Speech-to-Text Voice Input",
        purpose: "Dictation / drafting / notes",
        currentChallenge:
            "Lawyers often need to type long notes, draft instructions, case facts or client communication manually.",
        aiAction:
            "AI converts voice input into text and can convert dictated points into notes, drafts or email content.",
        benefit:
            "Quick capture of legal thoughts, faster note preparation and easier drafting support.",
        implementation:
            "Can support voice-based case notes, draft instructions, hearing notes, client meeting summaries and multilingual input where required.",
    },
    {
        id: 11,
        icon: <LockKeyhole size={20} />,
        name: "User Roles & Permissions",
        purpose: "Confidentiality / access control",
        currentChallenge:
            "Manual access control creates risk of accidental misuse or unnecessary exposure of confidential case data.",
        aiAction:
            "Role-based access controls who can view, edit, approve, download or manage specific data.",
        benefit:
            "Better confidentiality, safer collaboration and controlled access for admin, senior, junior and accounts users.",
        implementation:
            "Can include senior lawyer, junior lawyer, admin, accounts and read-only user roles with audit trails.",
    },
    {
        id: 12,
        icon: <Gavel size={20} />,
        name: "Judgment Summary Tool",
        purpose: "Judgment review / legal research",
        currentChallenge:
            "Long judgments and court orders take time to read, understand and convert into useful legal points.",
        aiAction:
            "AI summarizes court orders and judgments into issues, facts, findings, ratio, important observations and action points.",
        benefit:
            "Quicker understanding of rulings, faster research and better preparation for drafting and arguments.",
        implementation:
            "Can support PDF upload, key point extraction, case-law summary, legal issue tagging and matter-wise reference storage.",
    },
    {
        id: 13,
        icon: <BarChart3 size={20} />,
        name: "Legal Analytics Dashboard",
        purpose: "Management visibility",
        currentChallenge:
            "Partners and management may not have a consolidated view of active cases, pending work, billing and team performance.",
        aiAction:
            "Dashboard shows case status, pending tasks, hearing calendar, billing status and team workload.",
        benefit:
            "Better management decisions, stronger performance visibility and improved operational control.",
        implementation:
            "Can include matter status, client-wise work, pending hearings, team task load, invoice status and monthly activity dashboards.",
    },
    {
        id: 14,
        icon: <Search size={20} />,
        name: "Case Research",
        purpose: "Legal research / precedent search",
        currentChallenge:
            "Manual case-law research takes time and requires reviewing multiple judgments, legal references and previous matter notes.",
        aiAction:
            "AI searches relevant case laws, prior judgments, legal references and matter-specific precedents based on case facts or keywords.",
        benefit:
            "Faster legal research, stronger case preparation and better support for drafting arguments and opinions.",
        implementation:
            "Can support keyword search, fact-based research, cited answers, judgment summaries and internal knowledge-base search.",
    },
];

const valueAreas = [
    {
        icon: <FileText size={24} />,
        title: "Legal Drafting",
        text: "Draft notices, agreements, petitions, replies, case notes and client communication with review-ready AI assistance.",
    },
    {
        icon: <FolderOpen size={24} />,
        title: "Case Organization",
        text: "Connect emails, documents, tasks, hearings, invoices and client information into structured matter records.",
    },
    {
        icon: <MessageCircle size={24} />,
        title: "Client Communication",
        text: "Generate reply suggestions, reminders, WhatsApp/SMS updates and follow-up messages for better client handling.",
    },
    {
        icon: <ShieldCheck size={24} />,
        title: "Confidential AI",
        text: "Enable role-based access, controlled document handling and secure deployment options for sensitive legal data.",
    },
];

const implementationAreas = [
    {
        icon: <Database size={22} />,
        title: "Connect Existing Data",
        text: "The AI layer can connect with existing folders, Google Drive, case documents, emails and billing files without replacing current workflows.",
    },
    {
        icon: <Bot size={22} />,
        title: "Create Legal AI Assistants",
        text: "Role-specific assistants can support drafting, research, email replies, document checklists, billing and client updates.",
    },
    {
        icon: <ShieldCheck size={22} />,
        title: "Protect Confidential Data",
        text: "User roles, permissions, audit trails and private deployment options help maintain confidentiality and controlled access.",
    },
    {
        icon: <Settings size={22} />,
        title: "Implement In Phases",
        text: "Start with document search, drafting and reminders, then expand to billing, analytics, research and advanced automation.",
    },
];

const LawAndLegalAI = () => {
    const [selectedCountry, setSelectedCountry] = useState("India");

    const country = useContext(CountryContext);
    const websiteURL = window.location.origin;

    const [formVisible, setFormVisible] = useState(true);
    const [loading, setLoading] = useState(false);
    const [formError, setFormError] = useState("");
    const [phone, setPhone] = useState("");

    const [formData, setFormData] = useState({
        name: "",
        email: "",
        country: "",
        message: "",
        firmType: "Law and Legal",
    });

    const [errors, setErrors] = useState({
        name: "",
        email: "",
        phone: "",
        country: "",
        firmType: "",
        message: "",
    });

    useEffect(() => {
        AOS.init({
            duration: 900,
            easing: "ease-out-cubic",
            once: true,
            offset: 80,
        });
    }, []);

    useEffect(() => {
        if (countryOptions.includes(country)) {
            setSelectedCountry(country);
        } else {
            setSelectedCountry("India");
        }
    }, [country]);

    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormData({
            ...formData,
            [name]: value,
        });

        setErrors({
            ...errors,
            [name]: "",
        });

        if (name === "country") {
            setSelectedCountry(value);
            setPhone("");

            setErrors((prev) => ({
                ...prev,
                phone: "",
                country: "",
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        let newErrors = {
            name: "",
            email: "",
            phone: "",
            country: "",
            firmType: "",
            message: "",
        };

        let isValid = true;

        if (!formData.name.trim()) {
            newErrors.name = "Name is required";
            isValid = false;
        }

        const emailRegex = /^\S+@\S+\.\S+$/;

        if (!formData.email.trim()) {
            newErrors.email = "Email is required";
            isValid = false;
        } else if (!emailRegex.test(formData.email.trim())) {
            newErrors.email = "Invalid email format";
            isValid = false;
        }

        if (!phone) {
            newErrors.phone = "Phone number is required";
            isValid = false;
        } else if (!isValidPhoneNumber(phone)) {
            newErrors.phone = "Please enter a valid phone number";
            isValid = false;
        }

        if (!selectedCountry) {
            newErrors.country = "Please select country";
            isValid = false;
        }

        if (formData.message.trim().length > 300) {
            newErrors.message = "Maximum character limit is 300";
            isValid = false;
        }

        setErrors(newErrors);

        if (!isValid) return;

        const payload = {
            name: formData.name,
            email: formData.email.trim(),
            phone: phone,
            lookingFor: "Law and Legal",
            queryPageIndex: "Law_and_Legal_PAGE",
            message:
                (formData.message || "") +
                ` IP: ${country} dropdown: ${selectedCountry} | URL: ${websiteURL}`,
        };

        try {
            setLoading(true);
            setFormError("");

            const response = await fetch(ApiPath + "/tqenquiry", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });

            if (response.ok) {
                setFormData({
                    name: "",
                    email: "",
                    firmType: "Law and Legal",
                    message: "",
                });

                setPhone("");
                setFormVisible(false);

                setTimeout(() => {
                    setFormVisible(true);
                    setLoading(false);
                }, 5000);
            } else {
                setFormError("Something went wrong. Please try again.");
                setLoading(false);
            }
        } catch (error) {
            console.error(error);
            setFormError("Something went wrong. Please try again.");
            setLoading(false);
        }
    };

    const getCountryCode = (countryName) => {
        const map = {
            India: "IN",
            "United States": "US",
            Australia: "AU",
            Germany: "DE",
            Japan: "JP",
            Singapore: "SG",
            "New Zealand": "NZ",
        };

        return map[countryName] || "IN";
    };

    const StatCard = ({ icon, label, value, subText }) => (
        <div className="rounded-[2rem] border border-white/40 bg-white/80 backdrop-blur-xl shadow-[0_20px_70px_rgba(15,23,42,0.08)] p-6 lg:p-7 hover:-translate-y-1 transition-all duration-300">
            <div className="w-14 h-14 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center">
                {icon}
            </div>

            <p className="mt-5 text-xs uppercase tracking-[0.18em] text-slate-500 font-bold">
                {label}
            </p>

            <h3 className="mt-3 text-2xl xl:text-2xl font-black text-slate-900 leading-tight break-words">
                {value}
            </h3>

            <p className="mt-3 text-slate-500 leading-7 text-sm">
                {subText}
            </p>
        </div>
    );

    return (
        <>
            <Head />

            <div className="min-h-screen overflow-hidden bg-slate-50 text-slate-900 relative">
                {/* HERO */}
                <section className="relative py-20 lg:py-14 overflow-hidden">
                    <div className="absolute top-[-10rem] left-[-10rem] w-[30rem] h-[30rem] bg-blue-500/10 rounded-full blur-3xl"></div>
                    <div className="absolute bottom-[-10rem] right-[-10rem] w-[30rem] h-[30rem] bg-indigo-500/10 rounded-full blur-3xl"></div>

                    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 xl:gap-20 items-center">
                            <div className="lg:col-span-7 xl:col-span-7" data-aos="fade-right">
                                <div className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-blue-100 bg-blue-50 text-blue-700 text-sm font-bold">
                                    <Sparkles size={16} />
                                    AI Solutions For Law Firms
                                </div>

                                <h1 className="mt-8 text-5xl sm:text-6xl xl:text-6xl font-black leading-[0.95] tracking-[-0.05em] text-slate-900">
                                    AI Automation
                                    <span className="block mt-3 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                                        For Legal & Law Firms
                                    </span>
                                </h1>

                                <p className="mt-4 text-lg leading-9 text-slate-600 max-w-2xl">
                                    Modernize legal practice operations with AI-enabled drafting, case search,
                                    email extraction, document management, legal research, task tracking,
                                    billing assistance and secure client communication.
                                </p>

                                <div className="mt-10 flex flex-col sm:flex-row gap-4">
                                    <a
                                        href="#contact-form"
                                        className="inline-flex items-center justify-center gap-2 rounded-2xl px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold shadow-xl hover:-translate-y-1 transition-all duration-300"
                                    >
                                        Get AI Consultation
                                        <ArrowRight size={18} />
                                    </a>

                                    <a
                                        href="#demo-video"
                                        className="inline-flex items-center justify-center gap-2 rounded-2xl px-8 py-4 border border-slate-200 bg-white/80 backdrop-blur-xl text-slate-800 font-bold shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                                    >
                                        Watch Demo
                                        <PlayCircle size={18} />
                                    </a>

                                    <a
                                        href="/assets/pdfs/AI Feature Benefit Report for Legal Practice.pdf"
                                        download
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center justify-center gap-2 rounded-2xl px-8 py-4 border border-slate-200 bg-white/80 backdrop-blur-xl text-slate-800 font-bold shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                                    >
                                        Download PDF
                                        <Download size={18} />
                                    </a>
                                </div>
                            </div>

                            <div className="relative lg:col-span-5 xl:col-span-5" data-aos="fade-left" data-aos-delay="150">
                                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-indigo-500/20 blur-3xl rounded-full"></div>

                                <div className="relative rounded-[2.5rem] border border-white/40 bg-white/80 backdrop-blur-xl shadow-[0_30px_90px_rgba(15,23,42,0.12)] p-4">
                                    <img
                                        src="/assets/images/lady-justice-statue-front-courthouse.jpg"
                                        alt="AI for Law Firms"
                                        className="w-full rounded-[2rem] object-cover"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* BENEFITS */}
                <section className="py-20">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid lg:grid-cols-12 gap-10 items-start">
                            <div className="lg:col-span-8" data-aos="fade-up">
                                <div className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-blue-100 bg-blue-50 text-blue-700 text-sm font-bold">
                                    <BarChart3 size={16} />
                                    AI Feature Benefits
                                </div>

                                <h2 className="mt-6 text-4xl lg:text-5xl font-black tracking-[-0.04em] leading-tight text-slate-900">
                                    Practical AI Benefits For Legal Teams
                                </h2>

                                <p className="mt-6 text-lg leading-9 text-slate-600 max-w-3xl">
                                    This section explains how each AI feature supports law firm workflows.
                                    The focus is on better drafting, stronger case organization, faster research,
                                    improved client communication, controlled access and management visibility.
                                </p>

                                <div className="mt-8 flex flex-wrap gap-3">
                                    {[
                                        "Smarter legal drafting",
                                        "Better case organization",
                                        "Faster client communication",
                                        "Secure document access",
                                        "Stronger management visibility",
                                    ].map((chip, index) => (
                                        <div
                                            key={index}
                                            className="rounded-full border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 shadow-sm"
                                        >
                                            {chip}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="lg:col-span-4" data-aos="zoom-in" data-aos-delay="100">
                                <div className="sticky top-28 rounded-[2rem] border border-white/40 bg-white/90 backdrop-blur-xl shadow-[0_20px_70px_rgba(15,23,42,0.08)] p-6 lg:p-8">
                                    <div className="w-16 h-16 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center">
                                        <Scale size={30} />
                                    </div>

                                    <h3 className="mt-6 text-2xl font-black text-slate-900">
                                        Not a Replacement For Lawyers
                                    </h3>

                                    <p className="mt-4 text-slate-600 leading-8">
                                        The AI system acts as a secure legal practice assistant. It supports
                                        drafting, research, reminders, document handling, billing and client
                                        communication while lawyers remain responsible for final review and legal judgment.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* BENEFIT CARDS */}
                        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                            <div data-aos="fade-up" data-aos-delay="0">
                                <StatCard
                                    icon={<FileText size={24} />}
                                    label="Drafting Assistance"
                                    value="Drafts & Templates"
                                    subText="Supports notices, agreements, petitions, replies, emails and matter-specific legal documents."
                                />
                            </div>

                            <div data-aos="fade-up" data-aos-delay="100">
                                <StatCard
                                    icon={<Search size={24} />}
                                    label="Research Assistance"
                                    value="Judgments & Case Laws"
                                    subText="Helps lawyers summarize judgments, search references and prepare better legal arguments."
                                />
                            </div>

                            <div data-aos="fade-up" data-aos-delay="200">
                                <StatCard
                                    icon={<ShieldCheck size={24} />}
                                    label="Confidential Legal AI"
                                    value="Private & Controlled"
                                    subText="Can be implemented with role-based access, permissions and secure deployment options."
                                />
                            </div>
                        </div>

                        {/* TABLE */}
                        <div
                            className="mt-14 overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-[0_20px_70px_rgba(15,23,42,0.06)]"
                            data-aos="fade-up"
                        >
                            <div className="px-6 lg:px-8 py-8 border-b border-slate-100">
                                <h3 className="text-2xl font-black text-slate-900">
                                    Feature-Wise AI Benefits Matrix
                                </h3>
                                <p className="mt-3 text-slate-600 leading-7">
                                    A clear business view of what each AI feature does and how it improves
                                    legal practice operations.
                                </p>
                            </div>

                            <div className="overflow-x-auto">
                                <table className="min-w-[1450px] w-full">
                                    <thead className="bg-gradient-to-r from-blue-600 to-indigo-600">
                                        <tr>
                                            <th className="px-6 py-5 text-left text-xs font-bold uppercase tracking-[0.18em] text-white">
                                                #
                                            </th>
                                            <th className="px-6 py-5 text-left text-xs font-bold uppercase tracking-[0.18em] text-white">
                                                AI Feature
                                            </th>
                                            <th className="px-6 py-5 text-left text-xs font-bold uppercase tracking-[0.18em] text-white">
                                                Purpose / Users
                                            </th>
                                            <th className="px-6 py-5 text-left text-xs font-bold uppercase tracking-[0.18em] text-white">
                                                Current Challenge
                                            </th>
                                            <th className="px-6 py-5 text-left text-xs font-bold uppercase tracking-[0.18em] text-white">
                                                What AI Does
                                            </th>
                                            <th className="px-6 py-5 text-left text-xs font-bold uppercase tracking-[0.18em] text-white">
                                                Law Firm Benefit
                                            </th>
                                            <th className="px-6 py-5 text-left text-xs font-bold uppercase tracking-[0.18em] text-white">
                                                Implementation Detail
                                            </th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {legalFeatures.map((item) => (
                                            <tr key={item.id} className="hover:bg-slate-50 transition-all duration-200">
                                                <td className="px-6 py-5 border-b border-slate-100 align-top">
                                                    <span className="w-10 h-10 rounded-full bg-blue-50 text-blue-600 font-black flex items-center justify-center">
                                                        {item.id}
                                                    </span>
                                                </td>

                                                <td className="px-6 py-5 border-b border-slate-100 align-top">
                                                    <div className="flex items-start gap-3">
                                                        <span className="mt-1 w-9 h-9 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                                                            {item.icon}
                                                        </span>
                                                        <strong className="text-slate-900 leading-7 block">
                                                            {item.name}
                                                        </strong>
                                                    </div>
                                                </td>

                                                <td className="px-6 py-5 border-b border-slate-100 align-top text-slate-600 font-medium leading-7">
                                                    {item.purpose}
                                                </td>

                                                <td className="px-6 py-5 border-b border-slate-100 align-top text-slate-500 leading-7">
                                                    {item.currentChallenge}
                                                </td>

                                                <td className="px-6 py-5 border-b border-slate-100 align-top text-slate-500 leading-7">
                                                    {item.aiAction}
                                                </td>

                                                <td className="px-6 py-5 border-b border-slate-100 align-top">
                                                    <span className="inline-flex rounded-2xl bg-blue-50 px-4 py-3 text-sm font-bold text-blue-700 leading-6">
                                                        {item.benefit}
                                                    </span>
                                                </td>

                                                <td className="px-6 py-5 border-b border-slate-100 align-top text-slate-500 leading-7">
                                                    {item.implementation}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <p className="mt-3 text-xs text-slate-500 leading-6">
                            *The above feature matrix is for business discussion and solution planning.
                            Final implementation scope depends on firm size, matter volume, document structure,
                            current software, user roles, security policy and workflow requirements.
                        </p>
                    </div>
                </section>

                {/* VALUE AREAS */}
                <section className="py-20 bg-white">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center max-w-3xl mx-auto" data-aos="fade-up">
                            <div className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-blue-100 bg-blue-50 text-blue-700 text-sm font-bold">
                                <Sparkles size={16} />
                                AI Opportunity Areas
                            </div>

                            <h2 className="mt-6 text-4xl lg:text-5xl font-black tracking-[-0.04em] leading-tight text-slate-900">
                                Where AI Creates Value In Legal Practice
                            </h2>

                            <p className="mt-6 text-lg leading-9 text-slate-600">
                                Start with practical legal workflows and then expand into research,
                                analytics and secure firm-wide automation.
                            </p>
                        </div>

                        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
                            {valueAreas.map((item, index) => (
                                <div
                                    key={index}
                                    className="rounded-[2rem] border border-slate-200 bg-slate-50 p-7 hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
                                    data-aos="fade-up"
                                    data-aos-delay={index * 100}
                                >
                                    <div className="w-14 h-14 rounded-2xl bg-blue-100 text-blue-600 flex items-center justify-center">
                                        {item.icon}
                                    </div>
                                    <h3 className="mt-6 text-xl font-black text-slate-900">
                                        {item.title}
                                    </h3>
                                    <p className="mt-4 text-slate-600 leading-7">
                                        {item.text}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* IMPLEMENTATION APPROACH */}
                <section className="py-20">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center max-w-3xl mx-auto" data-aos="fade-up">
                            <div className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-blue-100 bg-blue-50 text-blue-700 text-sm font-bold">
                                <Settings size={16} />
                                Implementation Approach
                            </div>

                            <h2 className="mt-6 text-4xl lg:text-5xl font-black tracking-[-0.04em] leading-tight text-slate-900">
                                AI Layer On Top Of Existing Legal Workflows
                            </h2>

                            <p className="mt-6 text-lg leading-9 text-slate-600">
                                The solution can be implemented in phases without disturbing existing legal practice operations.
                            </p>
                        </div>

                        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
                            {implementationAreas.map((item, index) => (
                                <div
                                    key={index}
                                    className="rounded-[2rem] border border-white/40 bg-white/80 backdrop-blur-xl shadow-[0_20px_70px_rgba(15,23,42,0.08)] p-7 hover:-translate-y-1 transition-all duration-300"
                                    data-aos="fade-up"
                                    data-aos-delay={index * 100}
                                >
                                    <div className="w-14 h-14 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center">
                                        {item.icon}
                                    </div>
                                    <h3 className="mt-6 text-xl font-black text-slate-900">
                                        {item.title}
                                    </h3>
                                    <p className="mt-4 text-slate-600 leading-7">
                                        {item.text}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* VIDEO */}
                <section id="demo-video" className="py-20 bg-white">
                    <div className="max-w-7xl mx-auto px-0 md:px-4 sm:px-6 lg:px-8">
                        <div className="text-center px-4" data-aos="fade-up">
                            <div className="inline-flex items-center gap-2 px-5 py-3 md:rounded-full border border-blue-100 bg-blue-50 text-blue-700 text-sm font-bold">
                                <PlayCircle size={16} />
                                AI Legal Workflow Demo
                            </div>

                            <h2 className="mt-6 text-4xl lg:text-5xl font-black tracking-[-0.04em] leading-tight text-slate-900">
                                See How AI Can Transform Legal Operations
                            </h2>

                            <p className="mt-6 text-lg leading-9 text-slate-600 max-w-3xl mx-auto">
                                Practical automation workflows for modern legal practices and law firms.
                            </p>
                        </div>

                        <div
                            className="mt-12 md:rounded-[2.5rem] bg-white border border-slate-200 shadow-[0_20px_70px_rgba(15,23,42,0.06)] p-5 lg:p-7"
                            data-aos="zoom-in-up"
                            data-aos-delay="150"
                        >
                            <div className="overflow-hidden md:rounded-[2rem] bg-black">
                                <video className="w-full aspect-video object-contain" controls autoPlay muted loop playsInline>
                                    <source src="/assets/videos/LawAndLegal.mp4" type="video/mp4" />
                                </video>
                            </div>
                        </div>
                    </div>
                </section>

                {/* CONTACT FORM SECTION */}
                <section id="contact-form" className="relative py-20 sm:py-24 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-indigo-50"></div>

                    <div className="relative max-w-7xl mx-auto px-0 md:px-4 sm:px-6 lg:px-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                            <div className="px-6" data-aos="fade-right">
                                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-100 text-blue-700 font-semibold text-sm border border-blue-200">
                                    <Sparkles size={16} />
                                    AI Consultation For Law Firms
                                </div>

                                <h2 className="mt-6 text-4xl sm:text-5xl font-black leading-tight text-slate-900">
                                    Let’s Build
                                    <span className="block bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                                        AI For Your Legal Practice
                                    </span>
                                </h2>

                                <p className="mt-6 text-lg text-slate-600 leading-8 max-w-xl">
                                    Automate legal drafting, improve client communication, organize case documents,
                                    support legal research, reduce manual operations and modernize your law firm
                                    using secure AI-powered systems.
                                </p>

                                <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {[
                                        "Automate legal drafting",
                                        "Organize case documents",
                                        "Improve client response",
                                        "Secure AI workflow systems",
                                    ].map((item, index) => (
                                        <div
                                            key={index}
                                            className="flex items-center gap-3 bg-white border border-slate-200 rounded-2xl px-5 py-4 shadow-sm"
                                        >
                                            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center text-blue-600">
                                                <CheckCircle size={18} />
                                            </div>
                                            <span className="font-medium text-slate-700">{item}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="relative" data-aos="fade-left" data-aos-delay="100">
                                <div className="absolute -inset-2 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-[2rem] blur-xl opacity-20"></div>

                                <div className="relative bg-white/90 backdrop-blur-xl border border-white/40 md:rounded-[2rem] shadow-[0_25px_80px_rgba(0,0,0,0.08)] overflow-hidden">
                                    <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-8 text-white">
                                        <div className="flex items-center gap-4">
                                            <div className="w-14 h-14 rounded-2xl bg-white/15 flex items-center justify-center backdrop-blur-md">
                                                <Scale size={28} />
                                            </div>
                                            <div>
                                                <h3 className="text-2xl font-bold">Book AI Consultation</h3>
                                                <p className="text-blue-100 mt-1 text-sm">
                                                    Tell us about your law firm requirements
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="p-6 sm:p-8">
                                        {!formVisible && (
                                            <div className="bg-blue-50 border border-blue-200 text-blue-700 px-5 py-6 rounded-2xl mb-6">
                                                <div className="flex flex-col items-center text-center">
                                                    <CheckCircle size={52} className="text-blue-600 mb-4" />
                                                    <h3 className="text-xl font-bold mb-2">Thank You!</h3>
                                                    <p className="text-sm sm:text-base">Our team will contact you shortly.</p>
                                                </div>
                                            </div>
                                        )}

                                        {formError && (
                                            <div className="bg-red-50 border border-red-200 text-red-700 px-5 py-6 rounded-2xl mb-6">
                                                <div className="flex flex-col items-center text-center">
                                                    <XCircle size={48} className="text-red-500 mb-3" />
                                                    <h3 className="text-lg font-bold mb-2">Submission Failed</h3>
                                                    <p className="text-sm">{formError}</p>
                                                </div>
                                            </div>
                                        )}

                                        {formVisible && (
                                            <form onSubmit={handleSubmit} className="space-y-5">
                                                <div>
                                                    <input
                                                        type="text"
                                                        name="name"
                                                        placeholder="Full Name"
                                                        value={formData.name}
                                                        onChange={handleChange}
                                                        className={`w-full rounded-2xl border bg-slate-50 px-5 py-4 outline-none transition-all duration-300 ${errors.name
                                                                ? "border-red-500"
                                                                : "border-slate-200 focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100"
                                                            }`}
                                                    />
                                                    {errors.name && (
                                                        <p className="text-red-500 text-sm mt-2">{errors.name}</p>
                                                    )}
                                                </div>

                                                <div>
                                                    <div
                                                        className={`rounded-2xl border bg-slate-50 px-4 py-4 transition-all duration-300 ${errors.phone
                                                                ? "border-red-500"
                                                                : "border-slate-200 focus-within:border-blue-500 focus-within:bg-white focus-within:ring-4 focus:ring-blue-100"
                                                            }`}
                                                    >
                                                        <PhoneInput
                                                            key={selectedCountry}
                                                            countries={["IN", "US", "AU", "DE", "JP", "SG", "NZ"]}
                                                            defaultCountry={getCountryCode(selectedCountry)}
                                                            international
                                                            value={phone}
                                                            onChange={(value) => {
                                                                setPhone(value || "");
                                                                setErrors({ ...errors, phone: "" });
                                                            }}
                                                            placeholder="Enter phone number"
                                                        />
                                                    </div>
                                                    {errors.phone && (
                                                        <p className="text-red-500 text-sm mt-2">{errors.phone}</p>
                                                    )}
                                                </div>

                                                <div>
                                                    <input
                                                        type="email"
                                                        name="email"
                                                        placeholder="Email Address"
                                                        value={formData.email}
                                                        onChange={handleChange}
                                                        className={`w-full rounded-2xl border bg-slate-50 px-5 py-4 outline-none transition-all duration-300 ${errors.email
                                                                ? "border-red-500"
                                                                : "border-slate-200 focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100"
                                                            }`}
                                                    />
                                                    {errors.email && (
                                                        <p className="text-red-500 text-sm mt-2">{errors.email}</p>
                                                    )}
                                                </div>

                                                <div>
                                                    <select
                                                        id="country"
                                                        name="country"
                                                        className={`w-full rounded-2xl border bg-slate-50 px-5 py-4 outline-none transition-all duration-300 ${errors.country
                                                                ? "border-red-500"
                                                                : "border-slate-200 focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100"
                                                            }`}
                                                        onChange={handleChange}
                                                        value={selectedCountry}
                                                    >
                                                        {countryOptions.map((countryName) => (
                                                            <option key={countryName} value={countryName}>
                                                                {countryName}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    {errors.country && (
                                                        <p className="text-red-500 text-sm mt-2">{errors.country}</p>
                                                    )}
                                                </div>

                                                <div>
                                                    <textarea
                                                        rows={3}
                                                        name="message"
                                                        placeholder="Tell us which AI features or legal workflow problems you want to solve"
                                                        value={formData.message}
                                                        onChange={handleChange}
                                                        className={`w-full rounded-2xl bg-slate-50 px-5 py-4 outline-none transition-all duration-300 resize-none ${errors.message
                                                                ? "border border-red-500"
                                                                : "border border-slate-200 focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100"
                                                            }`}
                                                    />
                                                    {errors.message && (
                                                        <p className="text-red-500 text-sm mt-2">{errors.message}</p>
                                                    )}
                                                </div>

                                                <button
                                                    type="submit"
                                                    disabled={loading}
                                                    className={`w-full inline-flex items-center justify-center gap-2 rounded-2xl py-4 text-lg font-semibold text-white transition-all duration-300 ${loading
                                                            ? "bg-slate-400 cursor-not-allowed"
                                                            : "bg-gradient-to-r from-blue-600 to-indigo-600 shadow-xl hover:scale-[1.02]"
                                                        }`}
                                                >
                                                    {loading ? "Submitting..." : "Request AI Consultation"}
                                                    {!loading && <ArrowRight size={18} />}
                                                </button>
                                            </form>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <FooterNew />
            </div>
        </>
    );
};

export default LawAndLegalAI;